import { themeQuartz } from "ag-grid-community";

const portCountToString = (portCount) => {
  if (portCount === 1) return "Single";
  if (portCount === 2) return "Dual";
  if (portCount === 3) return "Triple";
  return String(portCount);
};

const formatAsCurrency = (number) => {
  if (number === null || number === undefined) return "";
  if (number === 0) return "-";
  return `$${Math.floor(number).toLocaleString()}`;
};

const formatZeroAsDash = (value) => (value === 0 ? "-" : value);

export const parsePositiveNumber = (value) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return 0;
  return parsed < 0 ? Math.abs(parsed) : parsed;
};

const parsePositiveCurrency = (value) => parsePositiveNumber(value);

export const buildPortHeaderName = (
  portCount,
  fallbackRange,
  fallbackLabel,
) => {
  const portLabel = portCountToString(portCount);
  if (fallbackRange && portCount) {
    return `Port Count: ${portLabel} Port Charger ${fallbackRange}`;
  }
  return `Port Count: ${portLabel} Port Charger ${fallbackLabel}`;
};

export const gridTheme = themeQuartz.withParams({
  borderColor: "#27272712",
  borderRadius: 8,
  browserColorScheme: "light",
  columnBorder: true,
  headerFontSize: 14,
  headerRowBorder: false,
  rowBorder: false,
  rowVerticalPaddingScale: 0.7,
  wrapperBorder: false,
  spacing: 10,
  headerVerticalPaddingScale: 0.6,
  headerTextColor: "#3D3D3D",
  headerBackgroundColor: "#F3F3F3",
});

export const createPhaseGridColumnDefs = ({
  siteOptions,
  validateSingleDualTriplePort,
  infrastructureLoanEnabled,
}) => {
  const editableIfNotPinned = (params) => !params.node.rowPinned;
  const makePortColumn = (field, headerName) => {
    const base = {
      field,
      cellStyle: { textAlign: "right" },
      editable: editableIfNotPinned,
      type: "number",
      valueFormatter: (params) => formatZeroAsDash(params.value),
      valueParser: (params) =>
        validateSingleDualTriplePort(
          params.colDef.headerName,
          params.oldValue,
          params.newValue,
        ),
    };
    return headerName ? { ...base, headerName } : base;
  };

  const makeCurrencyColumn = (field, headerName, extras = {}) => ({
    headerName,
    field,
    type: "currency",
    valueFormatter: (params) => formatAsCurrency(params.value),
    cellStyle: { textAlign: "right" },
    editable: editableIfNotPinned,
    valueParser: (params) => parsePositiveCurrency(params.newValue),
    ...extras,
  });

  return [
    {
      field: "year",
      editable: editableIfNotPinned,
      pinned: true,
    },
    {
      field: "site",
      editable: editableIfNotPinned,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: siteOptions,
      },
      cellStyle: (params) =>
        params.node.rowPinned ? { textAlign: "right" } : { textAlign: "left" },
      pinned: true,
    },
    makePortColumn("port_less_than_10_kw"),
    makePortColumn("port_10_20_kw", "Ports 10-20 kW"),
    makePortColumn("port_25_kw", "Ports 40 kW"),
    makePortColumn("port_180_200_kw", "Ports 180-200 kW"),
    makeCurrencyColumn("loan_amount", "Loan Amount", {
      hide: !infrastructureLoanEnabled,
    }),
    makeCurrencyColumn("capital_planning_funding", "Capital Planning Funding"),
    makeCurrencyColumn("trenching_costs", "Trenching Costs"),
    makeCurrencyColumn("upgrade_cost_utility", "Upgrade Cost (Utility)"),
    makeCurrencyColumn("upgrade_cost_customer", "Upgrade Cost (Customer)", {
      editable: editableIfNotPinned,
    }),
    makeCurrencyColumn(
      "procurement_management_cost",
      "Procurement Management Cost",
    ),
    makeCurrencyColumn(
      "estimated_public_works_engineering_costs",
      "Public Works Engineering Costs",
    ),
    makeCurrencyColumn("other_costs", "Other Costs"),
    {
      headerName: "Description of Other Costs",
      field: "other_costs_description",
      type: "text",
      cellStyle: { textAlign: "left" },
      editable: editableIfNotPinned,
    },
    makeCurrencyColumn("incentives", "Incentives, Rebates, and Credits", {
      headerTooltip: "Recorded as cost reduction at point-of-sale",
    }),
    makeCurrencyColumn("master_access_incentives", "Master Access Incentives", {
      headerTooltip: "Recorded as cost reduction at point-of-sale",
      cellStyle: {
        textAlign: "right",
      },
      hide: true,
    }),
    {
      headerName: "Total Incentives/Rebates/Credits",
      headerTooltip: "Total Incentives/Rebates/Credits",
      field: "totalIncentives",
      type: "currency",
      valueFormatter: (params) => formatAsCurrency(params.value),
      cellStyle: {
        textAlign: "right",
        fontWeight: "bold",
        color: "black",
        border: "none",
        background: "#F2F2F2",
      },
      editable: false,
    },
    {
      headerName: "Charger Equipment Cost",
      headerTooltip:
        "The upfront price of charging equipment, including the charger unit, mounting hardware, and cables/connectors.",
      field: "chargerEquipmentCost",
      editable: false,
      type: "currency",
      valueFormatter: (params) => formatAsCurrency(params.value),
      cellStyle: {
        textAlign: "right",
        fontWeight: "bold",
        color: "black",
        border: "none",
        background: "#F2F2F2",
      },
    },
    {
      headerName: "Charger Installation Cost",
      headerTooltip:
        "The cost to place the charger into service at a site, including electrical work, trenching, conduit installation, panel or transformer upgrades, and associated labor.",
      field: "installCost",
      editable: false,
      type: "currency",
      valueFormatter: (params) => formatAsCurrency(params.value),
      cellStyle: {
        textAlign: "right",
        fontWeight: "bold",
        color: "black",
        border: "none",
        background: "#F2F2F2",
      },
    },
    {
      headerName: "Total Charging Infrastructure Costs",
      headerTooltip:
        "Costs of purchasing, installing, and other support for charging infrastructure. Accounts for contingency.",
      field: "chargerInfraTotal",
      editable: false,
      type: "currency",
      valueFormatter: (params) => formatAsCurrency(params.value),
      cellStyle: {
        textAlign: "right",
        fontWeight: "bold",
        color: "black",
        border: "none",
        background: "#F2F2F2",
      },
    },
  ];
};
